import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { IconAlertTriangle } from '@rossum/ui/icons/tabler';
import {
  CircularProgress,
  Stack,
  SvgIcon,
  Typography,
  useTheme,
} from '@rossum/ui/material';
import {
  AreaPlot,
  ChartDataProviderPro,
  ChartsAxisHighlight,
  ChartsGrid,
  ChartsSurface,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
  ScatterPlot,
} from '@rossum/ui/x-charts-pro';
import { EMPTY_PLACEHOLDER } from '../../../core/utils';
import { AggregationPeriod, CommonXAxisProps } from '../../types';
import { ChartWrapper } from './ChartWrapper';
import { CustomLegend } from './CustomLegend';
import { CustomLegendTooltip } from './CustomLegendTooltip';
import { CustomTooltip } from './CustomTooltip';
import { getChartColors, getComposedChartStyles } from './styles';
import { PrecisionRateChartData } from './types';
import {
  CHART_HEIGHT,
  getCommonGradientAreaYAxisProps,
  getCommonYAxisProps,
} from './utils';

const PRECISION_SERIES_ID = 'precision';
const PRECISION_YAXIS_ID = 'precision-axis-y';

const PRECISION_TARGET_SERIES_ID = 'precision-target';
const PRECISION_TARGET_YAXIS_ID = 'precision-target-axis-y';

const ROUGH_PRECISION_SERIES_ID = 'rough-precision';
const ROUGH_PRECISION_YAXIS_ID = 'rough-precision-axis-y';

type PrecisionRateChartProps = {
  isLoading: boolean;
  precisionRateChartDataset: PrecisionRateChartData[];
  aggregationPeriod: AggregationPeriod;
  xAxisProps: CommonXAxisProps;
  averageTotalPrecision: string;
};

const portionRelevantRangeInSeries = (allSeries: number[]) => {
  const min = R.firstBy(allSeries, R.identity()) ?? 0;
  const offset = 2;
  return {
    min: R.clamp(min - offset, { min: 0 }),
    max: 100,
  };
};

// create an interval that spans to 4 ticks
const getGridIntervals = ({ min, max }: { min: number; max: number }) => {
  const diff = R.floor((max - min) / 3, 1);
  return R.pipe(
    // `end` value is not included in the array which is why we concat at the end of the pipe.
    R.range(min, { end: max - diff, step: diff }),
    R.map(R.round(1)),
    R.concat([max])
  );
};

const toPercentage = (value: number) => R.floor(value * 100, 1);

export const PrecisionRateChart = ({
  isLoading,
  precisionRateChartDataset,
  aggregationPeriod,
  xAxisProps,
  averageTotalPrecision,
}: PrecisionRateChartProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const {
    date,
    precision,
    hasRoughPrecision,
    precisionTarget,
    precisionValues,
    roughPrecision,
    areAllValuesZero,
  } = useMemo(() => {
    const precisionValues = precisionRateChartDataset.map(el =>
      el.precision.y === null ? null : toPercentage(el.precision.y)
    );
    return {
      areAllValuesZero: precisionValues.every(value =>
        R.isNumber(value) ? value === 0 : R.isNullish(value)
      ),
      hasRoughPrecision: precisionRateChartDataset.some(
        el => el.precision.y !== null && !el.precision.isQualityEstimate
      ),
      date: precisionRateChartDataset.map(el => new Date(el.date).getTime()),
      precisionValues,
      precision: precisionRateChartDataset.map(dataset => ({
        ...dataset.precision,
        y:
          dataset.precision.y !== null && dataset.precision.isQualityEstimate
            ? toPercentage(dataset.precision.y)
            : null,
      })),
      precisionTarget: precisionRateChartDataset.map(el =>
        el.precisionTarget ? toPercentage(el.precisionTarget) : null
      ),
      roughPrecision: precisionRateChartDataset.map(dataset => ({
        ...dataset.precision,
        y:
          dataset.precision.y !== null && !dataset.precision.isQualityEstimate
            ? toPercentage(dataset.precision.y)
            : null,
      })),
    };
  }, [precisionRateChartDataset]);

  const { surfaceStyles } = getComposedChartStyles({
    theme,
    primaryAxisId: PRECISION_SERIES_ID,
    secondaryAxisId: PRECISION_TARGET_SERIES_ID,
  });

  const { primary, secondary } = getChartColors(theme);

  const { min, max } = useMemo(() => {
    const uniqueSeriesValues = R.pipe(
      [...precisionValues, ...precisionTarget],
      R.filter(R.isNonNullish),
      R.unique()
    );

    return portionRelevantRangeInSeries(uniqueSeriesValues);
  }, [precisionValues, precisionTarget]);

  const getCommonPrecisionRateYaxisProps = () => {
    return {
      tickInterval: areAllValuesZero ? [0] : getGridIntervals({ min, max }),
      valueFormatter: (value: number) => `${value}%`,
      min,
      max,
      ...getCommonYAxisProps(theme.palette.text.secondary, { min }),
    } as const;
  };

  const markRadius = aggregationPeriod === 'days' ? 2 : 3;
  return (
    <ChartWrapper>
      <Typography variant="h5">
        ~{averageTotalPrecision}{' '}
        <Typography variant="h5" component="span" color="text.secondary">
          {intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.charts.precisionRate.title',
          })}
        </Typography>
      </Typography>
      <ChartDataProviderPro
        height={CHART_HEIGHT}
        series={[
          {
            data: precisionTarget,
            area: true,
            type: 'line',
            label: intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.precisionRate.series.precisionTarget',
            }),
            id: PRECISION_TARGET_SERIES_ID,
            yAxisId: PRECISION_TARGET_YAXIS_ID,
            color: secondary,
            valueFormatter: (value: number | null) =>
              R.isNumber(value) ? `${value}%` : EMPTY_PLACEHOLDER,
            curve: 'step',
            showMark: false,
            labelMarkType: 'circle',
          },
          {
            // @ts-expect-error type ScatterValueType = { y:number, x: number } of MUI this seems to accept `number | null` values in the next major updates
            data: precision,
            type: 'scatter',
            label: intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.precisionRate.series.precision',
            }),
            color: primary,
            id: PRECISION_SERIES_ID,
            yAxisId: PRECISION_YAXIS_ID,
            valueFormatter: value =>
              R.isNumber(value?.y) ? `${value.y}%` : null,
          },
          {
            // @ts-expect-error type ScatterValueType = { y:number, x: number } of MUI this seems to accept `number | null` values in the next major updates
            data: roughPrecision,
            type: 'scatter',
            label: intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.precisionRate.series.precision',
            }),
            id: ROUGH_PRECISION_SERIES_ID,
            yAxisId: ROUGH_PRECISION_YAXIS_ID,
            valueFormatter: value =>
              R.isNumber(value?.y) ? `${value.y}%` : null,
          },
        ]}
        yAxis={[
          {
            id: PRECISION_YAXIS_ID,
            position: 'right',
            ...getCommonPrecisionRateYaxisProps(),
          },
          {
            id: PRECISION_TARGET_YAXIS_ID,
            ...getCommonPrecisionRateYaxisProps(),
            ...getCommonGradientAreaYAxisProps(secondary, { min, max }),
          },
          {
            id: ROUGH_PRECISION_YAXIS_ID,
            ...getCommonPrecisionRateYaxisProps(),
          },
        ]}
        xAxis={[
          {
            data: date,
            scaleType: 'band',
            ...xAxisProps,
            valueFormatter: (value, context) =>
              R.isNumber(value)
                ? (xAxisProps.valueFormatter?.(
                    // watch out for local timezone
                    new Date(value).toISOString(),
                    context
                  ) ?? '')
                : '',
          },
        ]}
        margin={{ left: 0, right: 0 }}
      >
        <ChartsSurface
          sx={{
            ...surfaceStyles,
            [`& [data-series="${ROUGH_PRECISION_SERIES_ID}"]`]: {
              circle: {
                r: markRadius,
                stroke: primary,
                fill: theme.palette.background.default,
              },
            },
            [`& [data-series="${PRECISION_SERIES_ID}"]`]: {
              circle: {
                r: markRadius,
                stroke: primary,
                fill: primary,
              },
            },
          }}
        >
          <ChartsGrid horizontal />
          <ChartsXAxis
            disableLine
            disableTicks
            tickLabelStyle={{ fill: theme.palette.text.secondary }}
          />
          <ChartsYAxis
            disableLine
            disableTicks
            tickLabelStyle={{ fill: theme.palette.text.secondary }}
          />
          <ChartsAxisHighlight x="band" />

          <CustomTooltip
            getSeriesStyles={seriesId => {
              if (seriesId === ROUGH_PRECISION_SERIES_ID)
                return {
                  backgroundColor: t => t.palette.background.paper,
                  border: `1px solid ${primary}`,
                };

              if (seriesId === PRECISION_TARGET_SERIES_ID)
                return {
                  backgroundColor: secondary,
                };
              return {};
            }}
            seriesGroups={[[PRECISION_SERIES_ID, ROUGH_PRECISION_SERIES_ID]]}
          />

          <AreaPlot />
          <LinePlot />

          <MarkPlot />
          <ScatterPlot />
        </ChartsSurface>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <CustomLegend
            primarySeries={intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.precisionRate.series.precision',
            })}
            secondarySeries={intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.precisionRate.series.precisionTarget',
            })}
            tertiarySeries={
              hasRoughPrecision ? (
                <CustomLegendTooltip
                  label={intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.charts.precisionRate.series.roughPrecision',
                  })}
                  tooltip={intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.charts.precisionRate.series.roughPrecision.tooltip',
                  })}
                  Icon={
                    <SvgIcon color="warning" fontSize="small">
                      <IconAlertTriangle />
                    </SvgIcon>
                  }
                />
              ) : null
            }
          />
          {isLoading ? <CircularProgress size={16} /> : null}
        </Stack>
      </ChartDataProviderPro>
    </ChartWrapper>
  );
};
